/**
 * Slug generation utilities for SEO-friendly project URLs
 */

/**
 * Converts a project name to a URL-safe slug
 * Rules:
 * - lowercase
 * - trim
 * - remove diacritics (é -> e)
 * - replace '&' with 'and'
 * - replace whitespace/underscores with '-'
 * - remove anything not [a-z0-9-]
 * - collapse multiple '-' into single '-'
 * - strip leading/trailing '-'
 */
export function slugify(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  return name
    .toLowerCase()
    .trim()
    // Remove diacritics (accents)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Replace '&' with 'and'
    .replace(/&/g, 'and')
    // Replace whitespace and underscores with dashes
    .replace(/[\s_]+/g, '-')
    // Remove anything not alphanumeric or dash
    .replace(/[^a-z0-9-]/g, '')
    // Collapse multiple dashes
    .replace(/-+/g, '-')
    // Strip leading/trailing dashes
    .replace(/^-+|-+$/g, '');
}

/**
 * Ensures a slug is unique by appending a numeric suffix if needed
 * @param baseSlug - The base slug to make unique
 * @param existingSlugs - Set of existing slugs to check against
 * @returns A unique slug (baseSlug if available, or baseSlug-2, baseSlug-3, etc.)
 */
export function ensureUniqueSlug(
  baseSlug: string,
  existingSlugs: Set<string>
): string {
  if (!existingSlugs.has(baseSlug)) {
    return baseSlug;
  }

  let counter = 2;
  let candidate = `${baseSlug}-${counter}`;
  while (existingSlugs.has(candidate)) {
    counter++;
    candidate = `${baseSlug}-${counter}`;
  }

  return candidate;
}

/**
 * Generates a unique slug for a project, with fallback to ID if needed
 * @param name - Project name
 * @param id - Project ID (used as fallback if slug generation fails)
 * @param existingSlugs - Set of existing slugs
 * @returns A unique slug
 */
export function generateProjectSlug(
  name: string,
  id: string | number,
  existingSlugs: Set<string>
): string {
  const baseSlug = slugify(name);
  
  // If slugify produced nothing, fallback to ID-based slug
  if (!baseSlug) {
    const idSlug = `project-${id}`;
    return ensureUniqueSlug(idSlug, existingSlugs);
  }

  return ensureUniqueSlug(baseSlug, existingSlugs);
}

/**
 * Helper to get project URL from a project object
 * @param project - Project object with slug or id
 * @returns URL path like /projects/damac-lagoons
 */
export function getProjectUrl(project: { slug?: string; id: string | number }): string {
  if (project.slug) {
    return `/projects/${project.slug}`;
  }
  // Fallback to ID route (will redirect to slug route)
  return `/property/${project.id}`;
}
